import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {generateChartId, getWindowLocationOrigin} from 'charts/chart-content/PlotlyChartUtils';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import staticURL from 'lib/staticURL';
import getTestProps from 'test/lib/cypress/getTestProps';
import {error} from 'util/Logging';

const getPostMessageOrigin = (): string =>
  BenchlingConfig.CDN_DOMAIN ? `https://${BenchlingConfig.CDN_DOMAIN}` : '/';

export interface SecurePlotlyChartProps {
  libraryConfig: unknown;
  /**
   * Called when a chart is unloaded, due to browser resource limitations.
   *
   * Charts that use WebGL may be unloaded if the browser reaches its maximum number of WebGL contexts.
   */
  onChartForceUnloaded?: () => void;
  /**
   * Called when multiple points are selected via lasso or box selection.
   */
  onPointsSelected?: (rowIndices: Array<number>) => void;
  /**
   * Set of selected row indices to highlight in the chart
   */
  selectedRowIndices?: Set<number>;
  /**
   * Whether to allow lasso selection mode on this chart.
   * When true and the visual flagging feature flag is enabled, lasso selection will be available.
   */
  allowLassoSelection?: boolean;
}

/**
 * Securely render untrusted chart content.
 *
 * We use an iFrame to isolate Plotly chart rendering with
 * tight CSP to prevent data exfiltration and XSS attacks.
 *
 * This component renders chart data securely by passing it to an iframe
 * hosted on the CDN using postMessage. Analogous to PlotlyChart but with
 * additional security isolation.
 *
 * @package
 */
export function SecurePlotlyChart({
  libraryConfig,
  onChartForceUnloaded,
  onPointsSelected,
  selectedRowIndices,
  allowLassoSelection = false,
}: SecurePlotlyChartProps): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [iframeURL, setIframeURL] = useState('about:blank');
  const chartIdRef = useRef<string>(generateChartId());
  const iframeTimeoutRef = useRef<number | null>(null);
  const chartRenderTimeoutRef = useRef<number | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const {enableAnalysisToolVisualFlagging} = useRolloutFlags();

  // Conditionally add lasso dragmode when feature flag is enabled and lasso selection is allowed
  const chartDataToSend = useMemo(() => {
    const flagEnabled = enableAnalysisToolVisualFlagging;
    const enableLasso = flagEnabled && allowLassoSelection;
    if (!enableLasso || !libraryConfig) {
      return libraryConfig;
    }
    const config = libraryConfig as {layout?: Record<string, unknown>};
    return {
      ...config,
      layout: {
        ...config.layout,
        dragmode: 'lasso',
      },
    };
  }, [libraryConfig, enableAnalysisToolVisualFlagging, allowLassoSelection]);

  useEffect(() => {
    // Only fetch the iframe URL once, this prevents the error log from firing too many times.
    const baseURL = staticURL('/static/charts/plotly-content.html');
    const urlWithChartIdAndOrigin = `${baseURL}?chartId=${encodeURIComponent(chartIdRef.current)}&origin=${encodeURIComponent(getWindowLocationOrigin())}`;
    setIframeURL(urlWithChartIdAndOrigin);

    // Set 10s timeout for iframe READY message
    if (isIframeReady) {
      return;
    }
    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
    }
    iframeTimeoutRef.current = window.setTimeout(() => {
      if (!isIframeReady && isMountedRef.current) {
        error('SecurePlotlyChart iframe READY timeout', {
          tags: {component: 'SecurePlotlyChart'},
          extra: {chartId: chartIdRef.current},
        });
        onChartForceUnloaded?.();
      }
    }, 10_000);
  }, [isIframeReady, onChartForceUnloaded]);

  // Listen for iframe ready message
  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      // Only handle messages from our specific iframe
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data.type === 'CHART_IFRAME_READY' && event.data.chartId === chartIdRef.current) {
        setIsIframeReady(true);
        // Clear iframe timeout since we got the READY message
        if (iframeTimeoutRef.current) {
          clearTimeout(iframeTimeoutRef.current);
          iframeTimeoutRef.current = null;
        }
      } else if (
        event.data.type === 'CHART_WEBGL_CONTEXT_LOST' &&
        event.data.chartId === chartIdRef.current
      ) {
        onChartForceUnloaded?.();
      } else if (event.data.type === 'CHART_RENDERED_ACK' && event.data.chartId === chartIdRef.current) {
        // Clear chart render timeout since we got the ack
        if (chartRenderTimeoutRef.current) {
          clearTimeout(chartRenderTimeoutRef.current);
          chartRenderTimeoutRef.current = null;
        }
      } else if (event.data.type === 'CHART_RENDER_ERROR_ACK' && event.data.chartId === chartIdRef.current) {
        // Chart failed to render, trigger static chart fallback
        if (chartRenderTimeoutRef.current) {
          clearTimeout(chartRenderTimeoutRef.current);
          chartRenderTimeoutRef.current = null;
        }
        error(`Chart render failed: ${event.data.error}`, {
          tags: {component: 'SecurePlotlyChart'},
          extra: {chartId: chartIdRef.current},
        });
        onChartForceUnloaded?.();
      } else if (event.data.type === 'CHART_POINTS_SELECTED' && event.data.chartId === chartIdRef.current) {
        onPointsSelected?.(event.data.rowIndices);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onChartForceUnloaded, onPointsSelected]);

  // Send chart data when iframe is ready and we have data
  useEffect(() => {
    if (isIframeReady && chartDataToSend && iframeRef.current?.contentWindow) {
      // Set 10s timeout for chart render acknowledgement
      if (chartRenderTimeoutRef.current) {
        clearTimeout(chartRenderTimeoutRef.current);
      }
      chartRenderTimeoutRef.current = window.setTimeout(() => {
        // If timeout fires, it means we didn't get the ack (timeout wasn't cleared)
        if (isMountedRef.current) {
          error('SecurePlotlyChart chart render ack timeout', {
            tags: {component: 'SecurePlotlyChart'},
            extra: {chartId: chartIdRef.current},
          });
          onChartForceUnloaded?.();
        }
      }, 10_000);

      iframeRef.current.contentWindow.postMessage(
        {
          type: 'RENDER_CHART',
          chartId: chartIdRef.current,
          chartData: chartDataToSend,
        },
        getPostMessageOrigin()
      );
    }
  }, [isIframeReady, chartDataToSend, onChartForceUnloaded]);

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (iframeTimeoutRef.current) {
        clearTimeout(iframeTimeoutRef.current);
      }
      if (chartRenderTimeoutRef.current) {
        clearTimeout(chartRenderTimeoutRef.current);
      }
    },
    []
  );

  return (
    <iframe
      title="Plotly Chart"
      ref={iframeRef}
      src={iframeURL}
      sandbox="allow-scripts allow-same-origin"
      referrerPolicy="no-referrer"
      className={css(styles.container)}
      {...getTestProps({component: 'PlotlyChart', element: 'root'})}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
});
